/**
 * Injects tracking into a page's raw HTML.
 *
 * Speed rule:    nothing heavy downloads before first paint.
 * Accuracy rule: fbq() and gtag() exist from the first line of the page, so any
 *                event the page fires is queued and replayed, never lost.
 *
 * Two bugs this fixes vs. the previous version:
 *   1. The fbq stub was created inside a 1.5s timer, so fbq did not exist while
 *      the page was running. Any fbq() call from the page HTML threw and was lost.
 *   2. Only PageView was sent. The page-type conversion events (Lead, etc.) that
 *      the old serve-page.js used to fire were dropped entirely.
 */

// Conversion pages load the libraries immediately — a fast redirect must not drop the event.
const IMMEDIATE_TYPES = new Set(['thank-you']);

const eventsFor = (pageType) => {
  switch (pageType) {
    case 'thank-you':
      return ["fbq('track','Lead');"];
    case 'sales':
    case 'tools':
      return ["fbq('track','ViewContent');"];
    case 'form':
      return ["fbq('track','Contact');"];
    // 'opt-in' and 'trip-wire' intentionally send PageView only.
    // Fire Purchase from the real checkout confirmation, never from a page view.
    default:
      return [];
  }
};

export const injectTracking = (html, pageData, siteData) => {
  if (!html) return '';

  const pixelId = String(siteData?.meta_pixel_id || '').replace(/\D/g, '');
  const gaRaw = String(siteData?.ga_tracking_id || '').trim();
  const gaId = /^G-[A-Z0-9]+$/i.test(gaRaw) ? gaRaw : '';

  // If the page HTML carries its own base pixel/tag, don't load a second copy.
  const usePixel = pixelId && !/fbevents\.js/i.test(html);
  const useGa = gaId && !/googletagmanager\.com\/gtag/i.test(html);

  const pageType = pageData?.page_type;
  let stubs = '';
  const libs = [];

  if (usePixel) {
    // Standard Meta queue stub WITHOUT the script download. Calls made before the
    // library arrives are queued, then replayed by fbevents.js once it loads.
    stubs +=
      "!function(f){if(f.fbq)return;var n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};" +
      "if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[]}(window);" +
      `fbq('init','${pixelId}');fbq('track','PageView');` +
      eventsFor(pageType).join('');
    libs.push('https://connect.facebook.net/en_US/fbevents.js');
  }

  if (useGa) {
    // window.gtag, not a scoped function, so page code can call gtag() too.
    stubs +=
      'window.dataLayer=window.dataLayer||[];window.gtag=function(){dataLayer.push(arguments)};' +
      `gtag('js',new Date());gtag('config','${gaId}');`;
    libs.push(`https://www.googletagmanager.com/gtag/js?id=${gaId}`);
  }

  // PubHost page analytics → POST to /.netlify/functions/track
  // (The old <script src=".../functions/track"> was the wrong path AND the wrong
  // method, which is where the "Unexpected token '<'" console error came from.)
  const beaconPayload =
    pageData?.id && pageData?.owner_id
      ? JSON.stringify(JSON.stringify({ page_id: pageData.id, owner_id: pageData.owner_id })).replace(/</g, '\\u003c')
      : '';

  const immediate = IMMEDIATE_TYPES.has(pageType);

  const script = `
<script>
${stubs}
(function(){
  var done=false;
  function loadLibs(){
    if(done)return;done=true;
    ${JSON.stringify(libs)}.forEach(function(src){
      var s=document.createElement('script');s.async=true;s.src=src;document.head.appendChild(s);
    });
  }
  function onLoaded(){
    ${immediate ? '' : 'setTimeout(loadLibs,1500);'}
    ${
      beaconPayload
        ? `try{if(navigator.sendBeacon){navigator.sendBeacon('/.netlify/functions/track',new Blob([${beaconPayload}],{type:'text/plain'}));}}catch(e){}`
        : ''
    }
  }
  ${immediate ? 'loadLibs();' : ''}
  if(document.readyState==='complete'){onLoaded();}else{window.addEventListener('load',onLoaded);}
  // First tap, keypress or scroll starts the download, so anyone filling in the
  // opt-in form has the pixel fully loaded well before they hit submit.
  ['pointerdown','touchstart','keydown','scroll'].forEach(function(e){
    window.addEventListener(e,loadLibs,{once:true,passive:true});
  });
})();
</script>
${
  usePixel
    ? `<noscript><img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/></noscript>`
    : ''
}
`;

  if (html.includes('</head>')) {
    return html.replace('</head>', () => script + '</head>');
  }
  return script + html;
};